import React, { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SettingsLayout from '../components/Settings/SettingsLayout';
import { isValidAccountEmail, normalizeAccountEmail, toAccountResetErrorMessage } from './accountUtils';
import './Account.css';

export default function Account() {
  const location = useLocation();
  const { user, loading, sendResetEmail } = useAuth();
  const defaultEmail = useMemo(() => user?.email || '', [user]);
  const [email, setEmail] = useState(defaultEmail);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  const handleReset = async (e) => {
    e.preventDefault();
    const trimmed = normalizeAccountEmail(email);
    if (!trimmed) {
      setError('Enter the email for your account.');
      setStatus('');
      return;
    }

    if (!isValidAccountEmail(trimmed)) {
      setError('Enter a valid email address.');
      setStatus('');
      return;
    }

    setSubmitting(true);
    setError('');
    setStatus('Sending reset email...');

    try {
      await sendResetEmail(trimmed);
      setStatus('Password reset email sent. Check your inbox.');
    } catch (err) {
      setStatus('');
      setError(toAccountResetErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SettingsLayout activeTab="account">
        <div className="account-page">
          <p className="account-page__loading">Loading…</p>
        </div>
      </SettingsLayout>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return (
    <SettingsLayout activeTab="account">
      <div className="account-page">
        <div className="account-page__card">
          <header className="account-page__header">
            <h1 className="account-page__title">Account</h1>
            <p className="account-page__email">Signed in as {user.email}</p>
          </header>

          <section className="account-page__section" aria-labelledby="password-reset-heading">
          <h2 id="password-reset-heading" className="account-page__heading">
            Password reset
          </h2>
          <p className="account-page__hint">
            Send a secure Firebase reset link to your email and choose a new password.
          </p>

          <form className="account-page__form" onSubmit={handleReset}>
            <label htmlFor="account-reset-email" className="account-page__label">
              Email
            </label>
            <div className="account-page__row">
              <input
                id="account-reset-email"
                type="email"
                className="account-page__input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@example.com"
              />
              <button type="submit" className="account-page__btn" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send reset email'}
              </button>
            </div>
          </form>

          {status && <p className="account-page__status" role="status">{status}</p>}
          {error && <p className="account-page__error" role="alert">{error}</p>}
        </section>

        </div>
      </div>
    </SettingsLayout>
  );
}
